import type { ParsedSheet } from './parseSheet';
import type {
  LanguageRef,
  TranslationMemoryEntry,
} from '@src/repositories/TranslationMemoryEntryRepository';
import type { ImportCounts } from '@src/repositories/TranslationMemoryImportRepository';

/** Max data rows per file; over-cap is a fatal, whole-file rejection (DS7). */
export const IMPORT_ROW_CAP = 5000;

/**
 * Which column feeds each entry field. Column indices into the parsed rows;
 * `sourceTerm` is required, `translation` / `comment` optional (a blank
 * translation is a do-not-translate row, not an error — DS10).
 */
export interface ColumnMapping {
  sourceTerm: number;
  translation: number | null;
  comment: number | null;
}

/** What a single incoming row does to one target collection. */
export interface PreviewOutcome {
  /** `null` ⇒ Default (uncategorized). */
  collectionId: string | null;
  action: 'created' | 'overwritten' | 'skipped';
  /** Set for `overwritten` / `skipped` — the existing entry to update/leave. */
  existingEntryId?: string;
}

/** One parsed data row, classified per target collection (or flagged invalid). */
export interface PreviewRow {
  /** 1-based data-row number (for per-row error messages). */
  rowNumber: number;
  sourceTerm: string;
  /** Effective translation (== sourceTerm when DNT). */
  translation: string;
  comment: string;
  /** Blank translation ⇒ do-not-translate for the file's target language (DS10). */
  dnt: boolean;
  outcomes: PreviewOutcome[];
  /** Row-level problem (invalid) ⇒ nothing is written for this row (DS7). */
  error: string | null;
}

/**
 * Re-exported, not redeclared. This is the shape persisted on the import record,
 * so the repository owns it; a second structurally-identical copy here worked
 * only by accident and had to be kept in sync by hand (adding `failed` in
 * PLAT-10871 would have meant editing both). A preview reports the same tally it
 * will later persist, so it reads the one definition.
 */
export type { ImportCounts } from '@src/repositories/TranslationMemoryImportRepository';

/** The full pre-write preview: fatal file errors, per-row outcomes, and counts. */
export interface ImportPreview {
  /** Fatal, whole-file problems — non-empty ⇒ nothing is importable. */
  fileErrors: string[];
  rows: PreviewRow[];
  counts: ImportCounts;
  invalidRows: number;
}

/** The language pair + target collections chosen at upload (E10). */
export interface ImportTarget {
  sourceLanguage: LanguageRef;
  /** `null` ⇒ all target languages (E10). */
  translationLanguage: LanguageRef | null;
  /** Empty ⇒ Default (a single collection-less fan-out). */
  collectionIds: string[];
}

const norm = (value: string): string => value.trim().toLowerCase();

const HEADER_PATTERNS: Record<keyof ColumnMapping, RegExp> = {
  sourceTerm: /source[\s_-]?term|^term$|^source$|^original$/i,
  translation: /translation|^target$|translated|^rendering$/i,
  comment: /comment|^note$|^notes$/i,
};

/**
 * Auto-detect the column mapping from the header row (DS5). Recognized headers
 * win; otherwise fall back to positional (col 0 = source term, col 1 =
 * translation) so a header-less sheet still maps. The user can override before
 * previewing.
 */
export function autoMapColumns(headers: string[]): ColumnMapping {
  const findBy = (re: RegExp): number | null => {
    const idx = headers.findIndex(h => re.test(h));
    return idx === -1 ? null : idx;
  };
  const sourceTerm = findBy(HEADER_PATTERNS.sourceTerm) ?? 0;
  const translation =
    findBy(HEADER_PATTERNS.translation) ?? (headers.length > 1 ? 1 : null);
  const comment = findBy(HEADER_PATTERNS.comment);
  return { sourceTerm, translation, comment };
}

/**
 * Fuzzy-match a column-header string (e.g. "Spanish", "es", "Spanish (Spain)")
 * against the workspace's supported languages, so a header that names a language
 * pre-selects it. Matches on caption or BCP-47 value, in either containment
 * direction, case-insensitively.
 *
 * An exact caption/BCP-47 match wins outright. A loose (containment) match is
 * ambiguous — a bare "English" header matches every English locale — so a loose
 * match is biased toward `preferredId` (the showcase default source language)
 * when that language also matches, instead of grabbing the first in list order
 * (which would pick English (UK) over an English (US) default).
 */
export function matchLanguageByName(
  header: string | undefined,
  languages: LanguageRef[],
  preferredId?: string | null,
): LanguageRef | null {
  const needle = header?.trim().toLowerCase();
  if (!needle) return null;
  const matches = (l: LanguageRef, exact: boolean): boolean => {
    const caption = (l.caption ?? '').toLowerCase();
    const value = (l.value ?? '').toLowerCase();
    if (exact) return caption === needle || value === needle;
    return (
      (!!caption && (caption.includes(needle) || needle.includes(caption))) ||
      (!!value && (value.includes(needle) || needle.includes(value)))
    );
  };
  const exact = languages.find(l => matches(l, true));
  if (exact) return exact;
  const preferred = preferredId
    ? languages.find(l => l.id === preferredId && matches(l, false))
    : null;
  if (preferred) return preferred;
  return languages.find(l => matches(l, false)) ?? null;
}

/** Match key for overwrite detection: term + source lang + target lang + collection. */
const matchKey = (
  sourceTerm: string,
  sourceLanguageId: string,
  translationLanguageId: string | null,
  collectionId: string | null,
): string =>
  `${norm(sourceTerm)}|${sourceLanguageId}|${translationLanguageId ?? ''}|${collectionId ?? ''}`;

/**
 * Build the pre-write preview: validate structure, map columns, and classify
 * every (row × target collection) as created / overwritten / skipped, matching
 * against the showcase's existing entries (DS4). A blank translation is a
 * do-not-translate row (`translation === sourceTerm`, scoped to the file's
 * target language — DS10), never an error. Pure — performs no writes.
 */
export function buildPreview(
  sheet: ParsedSheet,
  mapping: ColumnMapping,
  target: ImportTarget,
  existingEntries: TranslationMemoryEntry[],
): ImportPreview {
  const fileErrors: string[] = [];

  if (sheet.rows.length === 0) {
    fileErrors.push('No data rows found in the file.');
  }
  if (sheet.rows.length > IMPORT_ROW_CAP) {
    fileErrors.push(
      `File has ${sheet.rows.length} rows — over the ${IMPORT_ROW_CAP.toLocaleString()}-row limit.`,
    );
  }
  if (mapping.sourceTerm < 0) {
    fileErrors.push('No source-term column is mapped.');
  }

  // Index existing entries by match key so per-row classification is O(1) (DS4).
  const targetLangId = target.translationLanguage?.id ?? null;
  const sourceLangId = target.sourceLanguage.id;
  const existingByKey = new Map<string, TranslationMemoryEntry>();
  for (const entry of existingEntries) {
    if (!entry.sourceLanguage) continue;
    existingByKey.set(
      matchKey(
        entry.sourceTerm,
        entry.sourceLanguage.id,
        entry.translationLanguage?.id ?? null,
        entry.collectionId,
      ),
      entry,
    );
  }

  // Empty collection selection ⇒ a single collection-less (Default) fan-out.
  const collectionTargets = target.collectionIds.length
    ? target.collectionIds
    : [null];

  const counts: ImportCounts = {
    created: 0,
    overwritten: 0,
    skipped: 0,
    failed: 0,
  };
  let invalidRows = 0;

  const cell = (row: string[], index: number | null): string =>
    index == null || index < 0 ? '' : (row[index] ?? '');

  // Track source terms already seen in THIS file (collection targets are
  // uniform per file, so a repeated term is a whole-row duplicate) so a later
  // duplicate row is flagged invalid rather than silently written as a second
  // `created` entry — which `existingByKey` (pre-existing entries only) can't
  // catch and would corrupt the glossary with duplicates.
  const firstRowByTerm = new Map<string, number>();

  const rows: PreviewRow[] = sheet.rows.map((row, i) => {
    const rowNumber = i + 1;
    const sourceTerm = cell(row, mapping.sourceTerm);
    const rawTranslation = cell(row, mapping.translation);
    const comment = cell(row, mapping.comment);

    if (!sourceTerm) {
      invalidRows += 1;
      return {
        rowNumber,
        sourceTerm,
        translation: '',
        comment,
        dnt: false,
        outcomes: [],
        error: 'Missing source term.',
      };
    }
    if (row.length > sheet.headers.length && sheet.headers.length > 0) {
      invalidRows += 1;
      return {
        rowNumber,
        sourceTerm,
        translation: rawTranslation,
        comment,
        dnt: false,
        outcomes: [],
        error: `Unexpected column count (${row.length}, expected ${sheet.headers.length}).`,
      };
    }

    const termKey = `${norm(sourceTerm)}|${sourceLangId}|${targetLangId ?? ''}`;
    const firstRow = firstRowByTerm.get(termKey);
    if (firstRow !== undefined) {
      invalidRows += 1;
      return {
        rowNumber,
        sourceTerm,
        translation: rawTranslation,
        comment,
        dnt: false,
        outcomes: [],
        error: `Duplicate source term (also on row ${firstRow}).`,
      };
    }
    firstRowByTerm.set(termKey, rowNumber);

    // DS10: a blank translation is a do-not-translate row — the term is pinned
    // verbatim for the file's target language.
    const dnt = rawTranslation === '';
    const translation = dnt ? sourceTerm : rawTranslation;

    const outcomes: PreviewOutcome[] = collectionTargets.map(
      (collectionId): PreviewOutcome => {
        const existing = existingByKey.get(
          matchKey(sourceTerm, sourceLangId, targetLangId, collectionId),
        );
        if (!existing) {
          counts.created += 1;
          return { collectionId, action: 'created' };
        }
        if (existing.translation === translation) {
          counts.skipped += 1;
          return {
            collectionId,
            action: 'skipped',
            existingEntryId: existing.id,
          };
        }
        counts.overwritten += 1;
        return {
          collectionId,
          action: 'overwritten',
          existingEntryId: existing.id,
        };
      },
    );

    return {
      rowNumber,
      sourceTerm,
      translation,
      comment,
      dnt,
      outcomes,
      error: null,
    };
  });

  return { fileErrors, rows, counts, invalidRows };
}
